import React, { useState, useRef } from 'react';
import { Container, Button, Alert, Badge, Modal } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import CustomPagination from '../components/common/customPagination';
import usePagination from '../hooks/listData';
import SearchField from '../components/common/searchField';
import DataTable from '../components/common/dataTable';
import { getAllBookings, deleteBooking } from '../services/bookingService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faInfoCircle, faTrash, faDownload, faFileExport } from '@fortawesome/free-solid-svg-icons';
import ExportModal from '../components/common/exportModal';
import eventImage from '../assets/images/mcube-ticket-2.jpg';
import kairaliImage from '../assets/images/Kairali-uk-tic.png';
import pranayaniImage from '../assets/images/PRANAYANILA-ticket-img.jpg';

const BookingsList = () => {
  const { data: bookings, search, setSearch, page, setPage, totalPages, loading, error, refetch } =
    usePagination(getAllBookings);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [currentQRIndex, setCurrentQRIndex] = useState(0);
  const ticketRef = useRef(null);
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await deleteBooking(id);
      console('hi');
      refetch();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleViewTicket = (booking) => {
    setSelectedBooking(booking);
    setCurrentQRIndex(0);
    setShowModal(true);
  };

  const handleQRNavigation = (direction) => {
    if (!selectedBooking || !selectedBooking.seats) return;
    
    if (direction === 'next') {
      setCurrentQRIndex((prev) => 
        prev < selectedBooking.seats.length - 1 ? prev + 1 : 0
      );
    } else {
      setCurrentQRIndex((prev) => 
        prev > 0 ? prev - 1 : selectedBooking.seats.length - 1
      );
    }
  };

  const downloadTicketsAsPDF = async () => {
    if (!selectedBooking || !selectedBooking.seats) return;

    try {
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < selectedBooking.seats.length; i++) {
        // Set current QR index for this ticket
        setCurrentQRIndex(i);
        
        // Wait for the DOM to update
        await new Promise(resolve => setTimeout(resolve, 200));

        // Get the ticket element
        const ticketElement = document.querySelector('.modal-ticket');
        if (!ticketElement) continue;

        try {
          // Add PDF-ready class for better rendering and hide download button
          ticketElement.classList.add('pdf-ready');
          
          // Hide download button during PDF generation
          const downloadButton1 = ticketElement.querySelector('.btn-success');
          if (downloadButton1) {
            downloadButton1.style.display = 'none';
          }

          // Capture the ticket as canvas
          const canvas = await html2canvas(ticketElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: ticketElement.offsetWidth,
            height: ticketElement.offsetHeight,
            logging: false,
            removeContainer: true
          });

          // Remove PDF-ready class and restore download button
          ticketElement.classList.remove('pdf-ready');
          
          // Restore download button visibility
          const downloadButton = ticketElement.querySelector('.position-absolute button');
          if (downloadButton) {
            downloadButton.style.display = '';
          }

          // Convert canvas to image
          const imgData = canvas.toDataURL('image/png', 1.0);

          // Calculate dimensions to fit the ticket on the page
          const imgWidth = pageWidth - 20; // 10mm margin on each side
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          // Center the image on the page
          const x = 10;
          const y = (pageHeight - imgHeight) / 2;

          // Add the ticket to PDF
          pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);

          // Add a new page if this isn't the last ticket
          if (i < selectedBooking.seats.length - 1) {
            pdf.addPage();
          }
        } catch (error) {
          console.error('Error generating PDF for ticket', i, ':', error);
        }
      }

      // Download the PDF
      const customerName = selectedBooking.customerName || selectedBooking.user?.name || 'customer';
      const fileName = `${customerName}_tickets_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error in PDF generation:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const getTicketImage = (eventTitle) => {
    if (!eventTitle) return eventImage;
    const eventTitleLower = eventTitle.toLowerCase();
    if (eventTitleLower.includes('fiesta')) {
      return kairaliImage;
    } else if (eventTitleLower.includes('pranayanila')) {
      return pranayaniImage;
    }
    return eventImage;
  };

  const getSectionBackgroundColor = (sectionName) => {
    const section = sectionName?.toLowerCase() || '';
    
    if (section.includes('vvip') || section.includes('premium') || section.includes('vv') || section.includes('gold')) {
      return 'radial-gradient(ellipse farthest-corner at right bottom, #FEDB37 0%, #FDB931 8%, #9f7928 30%, #8A6E2F 40%, transparent 80%), radial-gradient(ellipse farthest-corner at left top, #FFFFFF 0%, #FFFFAC 8%, #D1B464 25%, #5d4a1f 62.5%, #5d4a1f 100%)'; // Gold gradient
    } else if (section.includes('vip') || section.includes('VVV') || section.includes('vvv') || section.includes('silver')) {
      return 'linear-gradient(to bottom, #cccccc 0%, #474747 0%, #d9d9d9 52%, #3e3e3e 97%)'; // Silver gradient
    } else if (section.includes('reserved') || section.includes('diamond')) {
      return 'linear-gradient(135deg, #B9F2FF 0%, #00CED1 50%, #1E90FF 100%)'; // Diamond - Rich cyan to deep blue gradient
    } else if (section.includes('standard')) {
      return 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)'; // Standard - Modern teal gradient
    } else if (section.includes('general') || section.includes('balcony')) {
      return 'linear-gradient(135deg, #FFB347 0%, #FF8C42 50%, #FF6B35 100%)'; // Balcony - Warm orange to coral gradient
    } else {
      return 'linear-gradient(135deg, #E6E6FA 0%, #9370DB 100%)'; // Default purple gradient
    }
  };

  const handleSearchSubmit = () => {
    
    setPage(3);
  };

  const getScannedStatusBadge = (seats) => {
    if (!seats || !Array.isArray(seats)) return <Badge bg="secondary">N/A</Badge>;
    
    const scannedSeats = seats.filter(seat => seat.qrCodeUsed === true);
    const scannedCount = scannedSeats.length;
    const totalSeats = seats.length;
    
    if (scannedCount === 0) {
      return <Badge bg="warning">Not Scanned</Badge>;
    } else if (scannedCount === totalSeats) {
      // All seats scanned - show the latest scan time
      const latestScanTime = scannedSeats
        .map(seat => seat.qrCodeUsedAt)
        .filter(time => time)
        .sort()
        .pop();
      
      if (latestScanTime) {
        const scanDate = new Date(latestScanTime);
        const timeStr = scanDate.toLocaleString();
        return (
          <div>
            <Badge bg="success">All Scanned</Badge>
            <br />
            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
              Last: {timeStr}
            </small>
          </div>
        );
      } else {
        return <Badge bg="success">All Scanned</Badge>;
      }
    } else {
      // Partial scan - show scan times for scanned seats
      const scanTimes = scannedSeats
        .map(seat => seat.qrCodeUsedAt)
        .filter(time => time)
        .map(time => new Date(time).toLocaleString());
      
      return (
        <div>
          <Badge bg="info">{scannedCount}/{totalSeats} Scanned</Badge>
          {scanTimes.length > 0 && (
            <div>
              <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                {scanTimes.length === 1 ? 'Scanned:' : 'Scanned times:'}
                <br />
                {scanTimes.map((time, index) => (
                  <span key={index}>
                    {time}
                    {index < scanTimes.length - 1 && <br />}
                  </span>
                ))}
              </small>
            </div>
          )}
        </div>
      );
    }
  };

  const columns = [
    { header: "#", width: "5%" },
    { header: "Customer", width: "15%" },
    { header: "Event", width: "15%" },
    { header: "Seats", width: "20%" },
    { header: "Amount", width: "10%" },
    { header: "Scanned Status", width: "12%" },
    { header: "Booked At", width: "13%" },
    { header: "Actions", width: "10%" }
  ];

  const renderRow = (booking, index) => {
    // Format seats display - handle double-escaped JSON
    const formatSeats = (seats) => {
      if (!seats) return 'N/A';
      
      try {
        let seatsArray;
        
        if (typeof seats === 'string') {
          // Handle double-escaped JSON by parsing twice if needed
          let parsedSeats = seats;
          
          // First, try to parse normally
          try {
            seatsArray = JSON.parse(parsedSeats);
          } catch (firstError) {
            // If that fails, try parsing the escaped string
            // Replace escaped quotes with regular quotes
            parsedSeats = parsedSeats.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
            seatsArray = JSON.parse(parsedSeats);
          }
        } else if (Array.isArray(seats)) {
          seatsArray = seats;
        } else {
          return 'N/A';
        }
        
        return seatsArray.map(seat => 
          `${seat.section} - Seat No. ${seat.seatNumber}`
        ).join(', ');
      } catch (error) {
        console.error('Error parsing seats:', error);
        return 'N/A';
      }
    };

    // Determine customer info - either from user object or customer fields
    const customerName = booking.customerName || booking.user?.name || 'N/A';
    const customerEmail = booking.customerEmail || booking.user?.email || 'N/A';
    const customerPhone = booking.customerPhone || '';

    return (
      <tr key={booking._id}>
        <td>{(page - 1) * 10 + index + 1}</td>
        <td>
          <div>
            <strong>{customerName}</strong>
            <br />
            <small className="text-muted">{customerEmail}</small>
            {customerPhone && (
              <>
                <br />
                <small className="text-muted">{customerPhone}</small>
              </>
            )}
          </div>
        </td>
        <td>
          <div>
            <strong>{booking.event?.title || 'N/A'}</strong>
            <br />
            <small className="text-muted">
              {booking.event?.date ? new Date(booking.event.date).toLocaleDateString() : 'N/A'}
            </small>
          </div>
        </td>
        <td><small>{formatSeats(booking.seats)}</small></td>
        <td>
          {booking?.totalPrice ? `£ ${booking.totalPrice}` : 'N/A'}
        </td>
        <td>
          {getScannedStatusBadge(booking.seats)}
        </td>
        <td>{new Date(booking.bookingDate).toLocaleString()}</td>
        <td>
          <Button title='View Ticket' className="me-2 bg-transparent border-0 p-0" size="sm" onClick={() => handleViewTicket(booking)}>
            <FontAwesomeIcon icon={faInfoCircle} className="me-1" color='#000' size='lg' />
          </Button>
          <Button title='Delete Booking' variant="danger" className="bg-transparent border-0 p-0" size="sm" onClick={() => handleDelete(booking._id)}>
            <FontAwesomeIcon icon={faTrash} className="me-1" color='red' size='lg' />
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Bookings Management</h1>
        <Button 
          variant="success" 
          onClick={() => setShowExportModal(true)}
          className="d-flex align-items-center"
        >
          <FontAwesomeIcon icon={faFileExport} className="me-2" />
          Export Bookings
        </Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      <div className='pb-4'>
        <SearchField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
         
          onSubmit={handleSearchSubmit}
          placeholder="Search bookings..."
        />
      </div>
      
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <DataTable columns={columns} data={bookings} renderRow={renderRow} />
          <CustomPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      {/* Booking Ticket Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered id='bookingModal'>
        <Modal.Body className="p-0">
          <div className="position-absolute" style={{ top: '10px', right: '10px', zIndex: 1 }}>
            <button 
              type="button" 
              className="btn-close me-2" 
              onClick={() => setShowModal(false)} 
              aria-label="Close" 
            />
           
          </div>
          {selectedBooking && (
            <>
              <div ref={ticketRef} className="row d-flex flex-wrap bg-white rounded position-relative overflow-hidden modal-ticket">
                {/* Left Ticket Section */}
                <div className="d-flex col-md-8 px-0 position-relative">
                  <img src={getTicketImage(selectedBooking.event?.title)} alt="Event" className="img-fluid h-100"/>
                  
                </div>

                {/* Right Pricing Section */}
                <div 
                  className="col-md-4 p-md-4 text-start ticket-rhs position-relative d-flex align-items-center justify-content-center"
                  style={{
                    background: getSectionBackgroundColor(selectedBooking.seats?.[currentQRIndex]?.section),
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    position: 'relative',
                    // minHeight: '400px'
                  }}
                >
                  <div className="position-absolute" style={{ top: '10px', left: '20px', zIndex: 1 }}>
                  <Button
              variant="success"
              size="sm"
              onClick={downloadTicketsAsPDF}
              title="Download All Tickets"
            >
              <FontAwesomeIcon icon={faDownload} className="me-1" />
              Download Tickets
            </Button>
            </div>
                  
                  {/* QR Code Section */}
                  <div className="text-center mt-4">
                    {selectedBooking.seats && selectedBooking.seats.length > 0 && (
                      <>
                        {/* Seat Info Display */}
                        <div className="mb-3">
                          <h6 style={{ color: '#fff', marginBottom: '10px' }}>
                            {selectedBooking.seats[currentQRIndex]?.section} - Seat No. {selectedBooking.seats[currentQRIndex]?.seatNumber}
                          </h6>
                          {selectedBooking.seats.length > 1 && (
                            <p style={{ color: '#fff', fontSize: '14px' }}>
                              {currentQRIndex + 1} of {selectedBooking.seats.length} tickets
                            </p>
                          )}
                          
                        </div>
                        {/* QR Code Container */}
                        <div className="qr-container">
                          <QRCodeSVG
                            value={JSON.stringify({qrCodeToken: selectedBooking.seats[currentQRIndex]?.qrCodeToken || selectedBooking._id})}
                            size={150}
                            bgColor="#ffffff"
                            fgColor="#000000"
                            level="H"
                            includeMargin={true}
                            key={`qr-${selectedBooking.seats[currentQRIndex]?.qrCodeToken || selectedBooking._id}-${currentQRIndex}`}
                          />

                          {/* Navigation Arrows */}
                          {selectedBooking.seats.length > 1 && (
                            <>
                              <button
                                onClick={() => handleQRNavigation('prev')}
                                className="qr-nav-button prev"
                              >
                                <FontAwesomeIcon icon={faChevronLeft} />
                              </button>
                              <button
                                onClick={() => handleQRNavigation('next')}
                                className="qr-nav-button next"
                              >
                                <FontAwesomeIcon icon={faChevronRight} />
                              </button>
                            </>
                          )}
                        </div>

                        {/* Dots Indicator */}
                        {selectedBooking.seats.length > 1 && (
                          <div className="qr-dots">
                            {selectedBooking.seats.map((_, index) => (
                              <button
                                key={index}
                                onClick={() => setCurrentQRIndex(index)}
                                className={`qr-dot ${index === currentQRIndex ? 'active' : 'inactive'}`}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Export Modal */}
      <ExportModal 
        show={showExportModal} 
        onHide={() => setShowExportModal(false)} 
      />
    </Container>
  );
};

export default BookingsList;
